// Components for polling with colored options.
import { Checkmark } from '@carbon/icons-react';
import { Center, HorizBox, HoverView, Pad, PadBox, VertBox } from 'component/basics';
import { CTAButton, Tag, TextButton } from 'component/button';
import { DataVizText, TextField, UtilityText } from 'component/text';
import { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useCollection, useDatastore, usePersonaKey } from 'util/datastore';
import type { Table, TLabel } from 'util/stdtypes';
import { makeTestID, t } from 'util/stdtypes';

import {
    colorBorderPrimary,
    colorButtonPrimary,
    colorDataVizOther,
    colorDataVizScale3,
    colorSurfacePrimary,
    colorSurfaceSecondary,
    colorTextPrimary,
} from './color';
import { ColoredBall, ColoredBallPill } from './colorpill';
import { translateLabel, useLanguage } from './translation';

type PollSurveyThenResultsProps = {
    question?: string;
    optionLabels?: TLabel[];
    optionTexts?: string[];
    optionColors?: string[];
    onVote?: (vote: string | null, comment: string | null) => void;
    votes?: (string | undefined)[];
    myVote?: string;
    myComment?: string | null;
    showOtherOption?: boolean;
    showButton?: boolean;
    otherOptionLabel?: TLabel;
    otherOptionText?: string;
    otherOptionPlaceholder?: TLabel;
};

export function PollSurveyThenResults({
    question,
    optionLabels,
    optionTexts,
    optionColors,
    onVote,
    votes,
    myVote,
    myComment = null,
    showOtherOption = false,
    showButton,
    otherOptionLabel,
    otherOptionText,
    otherOptionPlaceholder,
}: PollSurveyThenResultsProps) {
    const s = PollSurveyThenResultsStyle;
    const votedForOther = myVote && myVote === (otherOptionLabel ?? otherOptionText);

    return (
        <View style={s.pollWrap}>
            <DataVizText text={question || ''} type='large' />
            <View style={s.subtitle}>
                <DataVizText label={myVote ? t('Your response') : t('Select one option')} type='small' />
            </View>
            {!myVote && (
                <PollSurvey
                    myComment={myComment}
                    myVote={myVote}
                    onVote={onVote}
                    optionColors={optionColors}
                    optionLabels={optionLabels}
                    optionTexts={optionTexts}
                    otherOptionLabel={otherOptionLabel}
                    otherOptionPlaceholder={otherOptionPlaceholder}
                    otherOptionText={otherOptionText}
                    showButton={showButton}
                    showOtherOption={showOtherOption}
                />
            )}
            {myVote && !votedForOther && <PollResult myVote={myVote} optionLabels={optionLabels} optionTexts={optionTexts} votes={votes} />}
            {myVote && votedForOther && <PollTextResult comment={myComment} myVote={myVote} />}
            {myVote && <Pad size={24} />}
            {myVote && (
                <Center>
                    <CTAButton label={t('Change my vote')} onPress={() => onVote?.(null, null)} wide />
                </Center>
            )}
        </View>
    );
}
const PollSurveyThenResultsStyle = StyleSheet.create({
    pollWrap: {
        backgroundColor: colorSurfaceSecondary,
        padding: 20,
        borderRadius: 12,
    },
    subtitle: {
        marginTop: 24,
        marginBottom: 12,
    },
});

export type PollPickerProps = {
    value: any;
    optionLabels?: TLabel[];
    optionTexts?: string[];
    optionColors?: string[];
    placeholder?: TLabel;
    onChangeValue: (value: number) => void;
};

export function PollPicker({ value, optionLabels, optionTexts, optionColors, placeholder, onChangeValue }: PollPickerProps) {
    const [expanded, setExpanded] = useState(!value);
    const options = optionLabels ?? optionTexts ?? [];

    function onVote(option) {
        const value = options.indexOf(option) + 1;
        setExpanded(false);
        onChangeValue(value);
    }

    if (expanded) {
        return (
            <View>
                <DataVizText label={placeholder} type='small' />
                <Pad size={12} />
                <PollSurvey
                    myVote={value}
                    onVote={onVote}
                    optionColors={optionColors}
                    optionLabels={optionLabels}
                    optionTexts={optionTexts}
                    showButton={false}
                />
            </View>
        );
    } else {
        return (
            <HorizBox center>
                <ColoredBallPill color={optionColors?.[value - 1]} label={optionLabels?.[value - 1]} text={optionTexts?.[value - 1]} />
                <Pad size={10} />
                <TextButton label={t('Change')} onPress={() => setExpanded(true)} type='tiny' />
            </HorizBox>
        );
    }
}

export type PollSurveyProps = {
    optionLabels?: TLabel[];
    optionTexts?: string[];
    optionColors?: string[];
    myVote?: string;
    myComment?: string | null;
    showButton?: boolean;
    onVote?: (option: string, otherValue: string | null) => void;
    showOtherOption?: boolean;
    otherOptionLabel?: TLabel;
    otherOptionText?: string;
    otherOptionPlaceholder?: TLabel;
};

// FIXME: This function is too large and should be broken up
/* eslint-disable-next-line max-lines-per-function */
export function PollSurvey({
    optionLabels,
    optionTexts,
    optionColors,
    myVote,
    myComment = null as string | null,
    showButton = true,
    onVote,
    showOtherOption = false,
    otherOptionLabel,
    otherOptionText,
    otherOptionPlaceholder,
}: PollSurveyProps) {
    const s = PollSurveyStyle;
    const options = optionLabels ?? optionTexts ?? [];
    const [selectedAnswer, setSelectedAnswer] = useState(myVote);
    const [textfieldValue, setTextfieldValue] = useState(myComment);

    const otherOptionValue = otherOptionLabel ?? otherOptionText;

    function onSelect(option) {
        setSelectedAnswer(option);
        if (!showButton) {
            onSubmit(option);
        }
    }

    function onSubmit(option) {
        const isOther = option === otherOptionValue;
        onVote?.(option, isOther ? textfieldValue : null);
    }

    return (
        <View role='list' style={[s.poll]}>
            <VertBox gap={12} role='list' wide>
                {options.map((option, idx) => (
                    <PollOptionButton
                        color={optionColors?.[idx]}
                        key={option}
                        label={optionLabels?.[idx]}
                        onPress={() => onSelect(option)}
                        selected={selectedAnswer === option}
                        text={optionTexts?.[idx]}
                    />
                ))}
                {showOtherOption && (
                    <View>
                        <PollOptionButton
                            label={otherOptionLabel}
                            onPress={() => onSelect(otherOptionValue)}
                            selected={selectedAnswer === otherOptionValue}
                            text={otherOptionText}
                        />
                        {selectedAnswer === otherOptionValue && (
                            <PadBox top={12}>
                                <TextField
                                    multiline={false}
                                    onChange={setTextfieldValue}
                                    placeholderLabel={otherOptionPlaceholder}
                                    testID='other-option'
                                    value={textfieldValue}
                                />
                            </PadBox>
                        )}
                    </View>
                )}
            </VertBox>
            {showButton && (
                <PadBox top={24}>
                    <CTAButton disabled={!selectedAnswer} label={t('Submit my vote')} onPress={() => onSubmit(selectedAnswer)} wide />
                </PadBox>
            )}
        </View>
    );
}
const PollSurveyStyle = StyleSheet.create({
    listWrapSurvey: {
        // paddingBottom: 15,
        // width: '100%',
    },
    poll: {
        alignItems: 'center',
    },
});

type PollOptionButtonProps = {
    label?: TLabel;
    text?: string;
    color?: string;
    onPress?: () => void;
    selected?: boolean;
    styledNextTopic?: boolean;
};

function PollOptionButton({ label, text, color, onPress, selected = false, styledNextTopic = false }: PollOptionButtonProps) {
    const s = PollOptionButtonStyle;

    return (
        <View role='listitem'>
            <HoverView
                ariaLabel={label}
                ariaText={text}
                hoverStyle={s.hover}
                onPress={onPress}
                role='button'
                style={[styledNextTopic ? s.buttonNextTopic : s.button]}
                testID={makeTestID({ label, text })}
            >
                {color && (
                    <PadBox right={8}>
                        <ColoredBall color={color} />
                    </PadBox>
                )}
                <View style={s.body}>
                    <UtilityText label={label} text={text} />
                </View>
                <View style={[s.rightIcon, selected ? s.iconSelected : null]}>
                    <Checkmark color='white' />
                </View>
            </HoverView>
        </View>
    );
}
const PollOptionButtonStyle = StyleSheet.create({
    button: {
        flexDirection: 'row',
        alignSelf: 'flex-start',
        alignItems: 'center',
        paddingRight: 10,
        paddingLeft: 20,
        paddingVertical: 10,
        width: '100%',
        borderRadius: 100,
        borderWidth: 1,
        backgroundColor: colorSurfacePrimary,
        borderColor: colorBorderPrimary,
    },
    buttonNextTopic: {
        flexDirection: 'row',
        alignSelf: 'flex-start',
        alignItems: 'center',
        paddingRight: 10,
        paddingLeft: 20,
        paddingVertical: 10,
        width: '100%',
        borderTopWidth: 1,
        borderColor: colorBorderPrimary,
    },
    hover: {
        borderColor: colorButtonPrimary,
    },
    body: {
        flex: 1,
    },
    rightIcon: {
        marginLeft: 10,
        borderColor: colorBorderPrimary,
        borderRadius: 100,
        borderWidth: 1,
        padding: 4,
    },
    iconSelected: {
        backgroundColor: colorButtonPrimary,
        borderColor: colorButtonPrimary,
    },
});

export type PollResultProps = { optionLabels?: any; optionTexts?: any; votes: any; myVote?: string | null };

export function PollResult({ optionLabels, optionTexts, votes, myVote }: PollResultProps) {
    const s = PollResultStyle;
    const options = optionLabels ?? optionTexts;
    const language = useLanguage();
    const tAriaLabel = options
        .map((option, index) => {
            // optionsLabels are translatable, but not optionsTexts, hence why we only translate if optionLabels is given
            const tLabel = optionLabels ? translateLabel({ label: option, language }) : optionTexts?.[index];
            const percent = getPercentAnswer(option, votes);
            if (myVote === option) {
                return translateLabel({
                    label: '{tLabel} (your vote): {percent}%',
                    language,
                    formatParams: { percent: percent || 0, tLabel },
                });
            }
            return translateLabel({ label: '{tLabel}: {percent}%', language, formatParams: { percent: percent || 0, tLabel } });
        })
        .join('; ');

    return (
        <View style={s.poll}>
            <View aria-label={tAriaLabel} role='img' style={s.listWrapResult}>
                {options.map((option) => (
                    <PollOptionPercentBar
                        isMine={myVote === option}
                        key={option}
                        label={optionLabels ? option : null}
                        percent={getPercentAnswer(option, votes)}
                        text={optionTexts ? option : null}
                    />
                ))}
            </View>
        </View>
    );
}
const PollResultStyle = StyleSheet.create({
    poll: {
        alignItems: 'center',
    },
    listWrapResult: {
        paddingHorizontal: 12,
        paddingTop: 16,
        paddingBottom: 4,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: colorBorderPrimary,
        backgroundColor: colorSurfaceSecondary,
        width: '100%',
    },
});

function getPercentAnswer(answer, votes) {
    const sameVote = votes.filter((vote) => {
        return vote === answer;
    });
    const percent = Math.round((sameVote.length * 100) / votes.length);
    return percent;
}

function PollOptionPercentBar({ label, text, percent = 0, isMine = true }) {
    const s = PollOptionPercentBarStyle;

    return (
        <View aria-hidden style={s.container}>
            <View style={s.bar}>
                <View style={[s.filledBar, isMine ? s.myBar : s.otherBar, { flex: percent }]} />
                <View style={{ flex: 100 - percent }} />
            </View>
            <View style={[s.barLabels, percent == 0 ? { paddingLeft: 0 } : null]}>
                <UtilityText color={colorTextPrimary} label={label} text={text} type='tiny' />
                <HorizBox center>
                    <View style={[s.tag, !isMine ? { display: 'none' } : null]}>
                        <Tag color={colorDataVizScale3} label={t('You')} overrideTinyTextColor strong type='tiny' />
                    </View>
                    <UtilityText color={colorTextPrimary} text={percent + '%'} type='small' weight='medium' />
                </HorizBox>
            </View>
        </View>
    );
}
const PollOptionPercentBarStyle = StyleSheet.create({
    container: {
        height: 36,
        marginBottom: 12,
        width: '100%',
    },
    tag: {
        marginRight: 8,
    },
    barLabels: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingLeft: 8,
    },
    bar: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 88,
        bottom: 0,
        flexDirection: 'row',
        alignItems: 'center',
    },
    filledBar: {
        height: 36,
        borderRadius: 4,
    },
    myBar: {
        backgroundColor: colorDataVizScale3,
    },
    otherBar: {
        backgroundColor: colorDataVizOther,
    },
});

// FIXME: myVote should not be any, and this is going to cause some translation issues
export type PollTextResultProps = { myVote: any; comment: any };

export function PollTextResult({ myVote, comment }: PollTextResultProps) {
    const s = PollTextResultStyle;
    return (
        <View style={s.poll}>
            <View style={s.textResultWrap}>
                <PollOptionButton label={!comment ? myVote : undefined} selected text={comment} />
            </View>
        </View>
    );
}
const PollTextResultStyle = StyleSheet.create({
    poll: {
        alignItems: 'center',
    },
    textResultWrap: {
        borderRadius: 8,
        backgroundColor: colorSurfaceSecondary,
        width: '100%',
    },
});

export type PollVote = {
    key: string;
    from: string;
    value: { label: string | null; comment?: string | null };
};

export type PollWidgetProps = {
    pollKey: Table<PollVote>;
    question: string;
    optionLabels?: TLabel[];
    optionTexts?: string[];
    showOtherOption?: boolean;
    otherOptionLabel?: TLabel;
    otherOptionText?: string;
    otherOptionPlaceholder?: TLabel;
};

export function PollWidget({
    pollKey,
    question,
    optionLabels,
    optionTexts,
    showOtherOption = false,
    otherOptionLabel,
    otherOptionText,
    otherOptionPlaceholder,
}: PollWidgetProps) {
    const datastore = useDatastore();
    const personaKey = usePersonaKey();
    const voteObjects = useCollection(pollKey);

    const votes = voteObjects.map((vote) => vote?.value?.label ?? undefined);
    const myVoteObject = voteObjects.find((v) => v?.from == personaKey);

    const myVote = myVoteObject?.value?.label ?? undefined;
    const myComment = myVoteObject?.value?.comment ?? null;

    const onVote = (vote: string | null, comment: string | null = null) => {
        const existing = datastore.getCollection(pollKey, { filter: { from: personaKey } });
        existing.forEach((r) => datastore.deleteObject(pollKey, r.key));
        const value: { label: string | null; comment?: string | null } = { label: vote ?? null };
        if (comment) value.comment = comment;
        datastore.addObject(pollKey, { value });
    };

    return (
        <PollSurveyThenResults
            myComment={myComment}
            myVote={myVote}
            onVote={datastore.needsLogin(onVote, 'vote')}
            optionLabels={optionLabels}
            optionTexts={optionTexts}
            otherOptionLabel={otherOptionLabel}
            otherOptionPlaceholder={otherOptionPlaceholder}
            otherOptionText={otherOptionText}
            question={question}
            showOtherOption={showOtherOption}
            votes={votes}
        />
    );
}
